import { GoogleGenAI, Type, ThinkingLevel } from "@google/genai";
import { 
    ImageSeoResult, 
    PlagiarismResult, 
    WriterConfig, 
    AuditResult, 
    RankResult, 
    CompetitorAnalysisResult, 
    ContentGapResult,
    SEONewsItem,
    KeywordResearchResult,
    AIModel 
} from '../types';

const ai = {
    models: {
        generateContent: async ({ model, contents, config }: { model: string, contents: any, config?: any }) => {
            const response = await fetch('/api/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    model,
                    prompt: contents,
                    config
                })
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return {
                text: data.text,
                candidates: data.candidates || [{ content: { parts: [{ text: data.text }] } }]
            };
        },
        generateContentStream: async ({ model, contents, config }: { model: string, contents: any, config?: any }) => {
            const response = await fetch('/api/generate-stream', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    model,
                    prompt: contents,
                    config
                })
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }
            
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            
            return {
                [Symbol.asyncIterator]: async function* () {
                    if (!reader) return;
                    try {
                        while (true) {
                            const { done, value } = await reader.read();
                            if (done) break;
                            const chunkText = decoder.decode(value, { stream: true });
                            yield { text: chunkText };
                        }
                    } finally {
                        reader.releaseLock();
                    }
                }
            };
        }
    }
};

const formatAiError = (e: any): string => {
    const message = e.message || String(e);
    if (message.includes("unregistered callers")) {
        return "Lỗi: Thiếu API Key (GEMINI_API_KEY). Vui lòng cấu hình API Key trong cài đặt hệ thống.";
    }
    if (message.includes("PERMISSION_DENIED")) {
        return "Lỗi: Quyền truy cập bị từ chối. Vui lòng kiểm tra lại API Key.";
    }
    if (message.includes("quota")) {
        return "Lỗi: Hết hạn mức sử dụng (Quota exceeded). Vui lòng thử lại sau hoặc nâng cấp gói.";
    }
    return message;
};

const GLOBAL_SYSTEM_INSTRUCTION = `
BẠN LÀ MỘT CONTENT CREATOR XUẤT SẮC - Viết bài như người thật, có tư duy phản biện và cảm xúc cá nhân.

QUY TẮC SỐNG CÒN ĐỂ TỐI ƯU HÓA AI SEARCH (SGE, Perplexity, ChatGPT Search) & VƯỢT TRÌNH AI DETECTION:
1. TỐI ƯU AI OVERVIEW (SGE): AI Search ưu tiên trích xuất câu trả lời trực tiếp. Hãy luôn trả lời thẳng vào vấn đề (Direct Answer) mạch lạc, súc tích trước khi diễn giải chi tiết. Sử dụng Bảng (Tables) và Danh sách (Bullet points) cho các dữ liệu quan trọng để AI dễ dàng trích xuất (Snippet-friendly).
2. INFORMATION GAIN (Giá trị độc bản): Cung cấp góc nhìn mới mẻ, số liệu thực tế, hoặc phân tích chuyên sâu mà các bài viết chung chung trên mạng không có. Tránh viết lại (rehash) những thông tin đã quá phổ biến.
3. TUYỆT ĐỐI CẤM nhắc đến bài viết trong bài viết: Bỏ qua các câu như "Bài viết dưới đây...", "Dưới đây là...", "Trong bài viết này...".
4. TUYỆT ĐỐI CẤM thẻ H1 (#): Không bao giờ sử dụng thẻ H1 trong nội dung Markdown. Chỉ sử dụng từ thẻ H2 (##) trở xuống.
5. TUYỆT ĐỐI CẤM cụm từ robot: "Trong bối cảnh...", "Ngày nay...", "Tóm lại là...", "Nhìn chung...".
6. TUYỆT ĐỐI CẤM tạo dòng kẻ ngang: Không dùng thẻ <hr /> hoặc các ký tự "---", "***" để chia phần. Hãy dùng thẻ H2 (##), H3 (###) phân chia rõ ràng.
7. PHONG CÁCH VIẾT & ĐỊNH DẠNG ĐẦY ĐỦ: Đan xen câu ngắn và câu dài một cách tự nhiên. Sử dụng đại từ nhân xưng linh hoạt. Trình bày đầy đủ, chi tiết các định dạng như Bảng so sánh (Markdown Tables), danh sách. Đối với các công thức toán học hoặc phương trình khoa học, hãy ghi chuẩn cú pháp LaTeX (sử dụng $...$ cho inline và $$...$$ cho block) để hiển thị chuyên nghiệp và đầy đủ nhất.
8. ĐỊNH DẠNG CHUNG: Chỉ trả về nội dung bằng Markdown sạch.
`;

export const cleanAndParseJson = <T>(text: string | undefined, defaultValue: T): T => {
    if (!text) return defaultValue;
    try {
        // Remove markdown code blocks
        let cleanText = text.replace(/```json\s*/gi, '').replace(/```/g, '').trim();
        
        // Attempt to find JSON object or array if mixed with text
        const firstBrace = cleanText.indexOf('{');
        const lastBrace = cleanText.lastIndexOf('}');
        const firstBracket = cleanText.indexOf('[');
        const lastBracket = cleanText.lastIndexOf(']');

        if (firstBracket !== -1 && lastBracket !== -1 && (firstBrace === -1 || firstBracket < firstBrace)) {
             cleanText = cleanText.substring(firstBracket, lastBracket + 1);
        } else if (firstBrace !== -1 && lastBrace !== -1) {
            cleanText = cleanText.substring(firstBrace, lastBrace + 1);
        }

        return JSON.parse(cleanText) as T;
    } catch (e) { 
        console.warn("JSON Parse Warning:", e);
        return defaultValue; 
    }
};


/**
 * Generic runner for Gemini prompts used by various modules.
 */
export const runGeminiPrompt = async (model: string, prompt: string, config?: any): Promise<{ text: string }> => {
    try {
        const response = await ai.models.generateContent({
            model: model,
            contents: prompt,
            config: config
        });
        return { text: response.text || '' };
    } catch (e: any) {
        throw new Error(formatAiError(e));
    }
};

const sanitizeModel = (model?: string): string => {
    if (!model) return 'gemini-4-argon';
    if (typeof model === 'string') {
        const m = model.toLowerCase();
        if (m.includes('flash')) return 'gemini-3.8-flash';
        if (m.includes('4') || m.includes('argon') || m.includes('pro')) return 'gemini-4-argon';
    }
    if (model === 'gemini-3.5-flash' || model === 'gemini-3.7-flash' || model === 'gemini-flash-latest') return 'gemini-3.8-flash';
    return 'gemini-4-argon';
};

export const fetchRealtimeSEONews = async (): Promise<SEONewsItem[]> => {
    const prompt = `
    VAI TRÒ: Chuyên gia tổng hợp tin tức SEO (SEO News Aggregator).
    NHIỆM VỤ: Sử dụng Google Search để tìm 3-4 tin tức, bài viết, hoặc xu hướng mới nhất (trong vòng 7 ngày qua) về các chủ đề sau: "SEO", "tối ưu hóa công cụ tìm kiếm", và "AI trong tìm kiếm" (Search AI, SGE).

    QUY TRÌNH:
    1. Tìm các bài viết uy tín, chất lượng cao từ các nguồn đáng tin cậy.
    2. Với mỗi bài viết, tóm tắt nội dung chính trong 1-2 câu súc tích, tập trung vào thông tin quan trọng nhất.
    3. Trích xuất URL nguồn chính xác của bài viết.

    YÊU CẦU ĐẦU RA (JSON Array - Chỉ trả về JSON, không Markdown):
    [
        {
          "title": "Tiêu đề hấp dẫn của bài viết gốc",
          "summary": "Tóm tắt ngắn gọn, dễ hiểu về nội dung chính.",
          "source": "URL đầy đủ của bài viết gốc"
        }
    ]
    `;
    
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-3.8-flash',
            contents: prompt,
            config: { 
                tools: [{ googleSearch: {} }],
                responseMimeType: "application/json" 
            }
        });
        return cleanAndParseJson<SEONewsItem[]>(res.text, []);
    } catch (e) {
        console.error("SEO News Fetch Error:", e);
        return [];
    }
};

export const performKeywordResearch = async (keyword: string): Promise<KeywordResearchResult> => {
    const prompt = `
    VAI TRÒ: Chuyên gia Trưởng Nghiên cứu Từ khóa & Phân tích SERP (Head of SEO Research).
    NHIỆM VỤ: Thực hiện nghiên cứu từ khóa chuyên sâu và khám phá cơ hội từ khóa tiềm năng cho từ khóa hạt giống: "${keyword}".

    QUY TRÌNH:
    1. Sử dụng Google Search để phân tích các kết quả tìm kiếm hàng đầu, hộp "Mọi người cũng hỏi" (People Also Ask), gợi ý tìm kiếm liên quan (Related Searches) và các biến thể ngữ nghĩa (LSI/Semantic).
    2. Đưa ra danh sách 15 - 25 từ khóa đa dạng, gồm:
       - Từ khóa hạt giống gốc & biến thể đồng nghĩa.
       - Từ khóa đuôi dài (Long-tail keywords) có tỷ lệ chuyển đổi cao.
       - Từ khóa câu hỏi (Question / Informational: "là gì", "cách làm", "ở đâu", "giá bao nhiêu", "như thế nào").
       - Từ khóa có ý định mua hàng / thương mại (Commercial / Transactional: "tốt nhất", "mua", "báo giá", "review", "dịch vụ", "khóa học").
    3. Với MỖI từ khóa trong danh sách:
       - keyword: Từ khóa chính xác (tiếng Việt hoặc tiếng Anh tùy theo ngữ cảnh từ khóa).
       - monthlyVolume: Ước tính lượng tìm kiếm hàng tháng (dạng chuỗi rõ ràng, ví dụ: "1,200", "5.4K", "18.5K", "350").
       - intent: Phân loại chính xác ý định người dùng: 'Informational', 'Commercial', 'Transactional', 'Navigational', 'Mixed'.
       - difficulty: Độ khó cạnh tranh KD từ 0 đến 100 (số nguyên). Dưới 30 là dễ rank, 30-60 trung bình, trên 60 cạnh tranh cao.
       - cpc: Giá thầu CPC ước tính Google Ads (ví dụ: "$0.45", "$1.20", "5.500 đ", "$2.80").
       - trend: Xu hướng tìm kiếm ("Tăng mạnh", "Tăng nhẹ", "Ổn định", "Mùa vụ").
       - category: Phân loại ("Long-tail", "Question", "Commercial", "Core").

    YÊU CẦU ĐẦU RA (JSON Array - Chỉ trả về JSON, không giải thích thêm):
    [
        {
          "keyword": "từ khóa được phân tích",
          "monthlyVolume": "5.4K",
          "intent": "Informational",
          "difficulty": 28,
          "cpc": "$0.65",
          "trend": "Tăng mạnh",
          "category": "Long-tail"
        },
        ...
    ]
    `;

    try {
        const res = await ai.models.generateContent({
            model: 'gemini-4-argon',
            contents: prompt,
            config: { 
                tools: [{ googleSearch: {} }],
                responseMimeType: "application/json" 
            }
        });
        
        const raw = cleanAndParseJson<any[]>(res.text, []);
        if (!Array.isArray(raw)) return [];

        const allowedIntents = ['Informational', 'Navigational', 'Commercial', 'Transactional', 'Mixed'];

        const normalized: KeywordResearchResult = raw.map((item: any) => {
            const rawKw = String(item.keyword || '').trim();
            if (!rawKw) return null;

            // Normalize difficulty
            let diff = 30;
            if (typeof item.difficulty === 'number') {
                diff = Math.min(100, Math.max(0, Math.round(item.difficulty)));
            } else if (typeof item.difficulty === 'string') {
                const parsed = parseInt(item.difficulty.replace(/[^0-9]/g, ''), 10);
                if (!isNaN(parsed)) diff = Math.min(100, Math.max(0, parsed));
            }

            // Normalize volume
            let vol = String(item.monthlyVolume || '1.2K').trim();
            if (!vol || vol === 'undefined') vol = '1.0K';

            // Normalize intent
            let intentVal = item.intent || 'Informational';
            if (!allowedIntents.includes(intentVal)) {
                const lower = String(intentVal).toLowerCase();
                if (lower.includes('info')) intentVal = 'Informational';
                else if (lower.includes('comm')) intentVal = 'Commercial';
                else if (lower.includes('trans')) intentVal = 'Transactional';
                else if (lower.includes('nav')) intentVal = 'Navigational';
                else intentVal = 'Informational';
            }

            // Normalize category
            let cat = item.category || 'Long-tail';
            if (rawKw.toLowerCase().includes('là gì') || rawKw.toLowerCase().includes('như thế nào') || rawKw.toLowerCase().includes('cách') || rawKw.toLowerCase().includes('tại sao')) {
                cat = 'Question';
            }

            return {
                keyword: rawKw,
                monthlyVolume: vol,
                intent: intentVal as any,
                difficulty: diff,
                cpc: item.cpc ? String(item.cpc) : '$0.50',
                trend: item.trend ? String(item.trend) : 'Ổn định',
                category: cat
            };
        }).filter(Boolean) as KeywordResearchResult;

        return normalized;
    } catch (e: any) {
        throw new Error(formatAiError(e));
    }
};


export type InlineAction = 'rewrite' | 'shorten' | 'expand' | 'simplify';

export const performInlineAction = async (action: InlineAction, text: string, context?: string): Promise<string> => {
    let instruction = '';
    switch (action) {
        case 'rewrite':
            instruction = 'Viết lại đoạn văn sau theo một cách khác nhưng giữ nguyên ý nghĩa. Trả về CHỈ đoạn văn đã viết lại.';
            break;
        case 'shorten':
            instruction = 'Rút gọn đoạn văn sau nhưng vẫn giữ lại những ý chính quan trọng nhất. Trả về CHỈ đoạn văn đã rút gọn.';
            break;
        case 'expand':
            instruction = 'Mở rộng và làm chi tiết hơn đoạn văn sau, thêm các thông tin hoặc ví dụ liên quan. Trả về CHỈ đoạn văn đã mở rộng.';
            break;
        case 'simplify':
            instruction = 'Viết lại đoạn văn sau cho đơn giản và dễ hiểu hơn với người đọc phổ thông. Trả về CHỈ đoạn văn đã đơn giản hóa.';
            break;
    }

    const prompt = `
    ${instruction}
    ${context ? `NGỮ CẢNH BÀI VIẾT: ${context}` : ''}
    ---
    ĐOẠN VĂN GỐC:
    "${text}"
    `;

    try {
        const res = await ai.models.generateContent({
            model: 'gemini-3.8-flash', // Ultra fast Flash for inline actions
            contents: prompt,
            config: { systemInstruction: "Bạn là một trợ lý biên tập viên. Chỉ trả về nội dung được yêu cầu, không thêm lời dẫn." }
        });
        // Clean up response, remove potential markdown quotes
        return (res.text || '').replace(/^"|"$/g, '').trim();
    } catch (e: any) { throw new Error(formatAiError(e)); }
};

export const generateOutline = async (config: WriterConfig): Promise<string> => {
    const prompt = `
    Lập dàn ý chi tiết (Outline) cho bài viết chuẩn SEO.
    CHỦ ĐỀ: "${config.topic}"
    TỪ KHÓA CHÍNH: "${config.keyword}"
    ĐỐI TƯỢỢNG: ${config.audience}
    MÔ HÌNH: ${config.framework}
    NGÔN NGỮ: ${config.language || 'Vietnamese'}
    
    YÊU CẦU:
    - Sử dụng định dạng Markdown (## cho H2, ### cho H3).
    - Tối ưu hóa cho AI Overview (SGE): Bắt buộc có phần "Key Takeaways" (Tóm tắt ý chính) ở đầu bài và phần "FAQ" (Câu hỏi thường gặp) ở cuối bài.
    - Bao gồm các phần mở bài, thân bài (triển khai chi tiết các ý chính), kết bài.
    - Phủ sóng Thực thể (Semantic Entities) tự nhiên trong các tiêu đề phụ.
    - Chỉ trả về phần nội dung dàn ý, không lời dẫn.
    `;

    try {
        const res = await ai.models.generateContent({
            model: 'gemini-3.8-flash', // Use Gemini 3.8 Flash for fast outline generation
            contents: prompt,
            config: { systemInstruction: "Bạn là chuyên gia lập cấu trúc nội dung." }
        });
        return res.text || '';
    } catch (e: any) { throw new Error(formatAiError(e)); }
};

export const generateSEOContent = async (config: WriterConfig, onUpdate?: (text: string) => void): Promise<string> => {
    const outlineInstruction = config.customOutline 
        ? `\nTUÂN THỦ NGHIÊM NGẶT DÀN Ý SAU (Không tự ý thay đổi cấu trúc H2/H3):\n${config.customOutline}\n`
        : '';

    const prompt = `
    CHỦ ĐỀ: "${config.topic}"
    TỪ KHÓA CHÍNH: "${config.keyword}"
    ${config.secondaryKeywords ? `TỪ KHÓA PHỤ (LSI/SEMANTIC): ${config.secondaryKeywords}` : ''}
    NGÔN NGỮ BÀI VIẾT: ${config.language || 'Vietnamese'}
    ĐỐI TƯỢỢNG: ${config.audience}
    VĂN PHONG: ${config.tone}
    ${config.brandVoiceSample ? `HỌC GIỌNG VĂN TỪ MẪU SAU: "${config.brandVoiceSample}"` : ''}
    ĐỘ DÀI: ~${config.length} từ.
    FRAMEWORK: ${config.framework}
    ${config.useEEAT ? 'YÊU CẦU E-E-A-T: Thể hiện kinh nghiệm thực tế và độ uy tín.' : ''}
    ${config.expertContext ? `DỮ LIỆU BỔ SUNG: ${config.expertContext}` : ''}
    ${config.competitorContext ? `\nCHIẾN LƯỢC ĐỐI THỦ:\n${config.competitorContext}\n` : ''}
    ${outlineInstruction}
    
    YÊU CẦU SEO & AI SEARCH ĐẶC BIỆT (DÀNH CHO SGE/PERPLEXITY):
    - Đưa ra "Direct Answer" (Câu trả lời trực tiếp, rõ ràng) ngay dưới các H2/H3 quan trọng để AI Overviews dễ trích xuất.
    - Sử dụng phong phú định dạng: Bullet points (Danh sách), Bảng biểu (Tables), Chữ đậm (Bold) để nhóm thông tin logic.
    - Phủ sóng đa dạng Thực thể (Semantic Entities) liên quan đến chủ đề để thỏa mãn Knowledge Graph của Google.
    
    YÊU CẦU QUAN TRỌNG: Viết sâu, trình bày Markdown (Chỉ H2, H3, Bold, List). KHÔNG ĐƯỢC CÓ THẺ H1.
    `;

    try {
        if (onUpdate) {
            const streamResult = await ai.models.generateContentStream({
                model: sanitizeModel(config.model),
                contents: prompt,
                config: { systemInstruction: GLOBAL_SYSTEM_INSTRUCTION }
            });
            let fullText = '';
            for await (const chunk of streamResult) {
                fullText += (chunk.text || '');
                onUpdate(fullText);
            }
            return fullText;
        } else {
            const response = await ai.models.generateContent({
                model: sanitizeModel(config.model),
                contents: prompt,
                config: { systemInstruction: GLOBAL_SYSTEM_INSTRUCTION }
            });
            return response.text || '';
        }
    } catch (e: any) { throw new Error(formatAiError(e)); }
};

export const synthesizeSeoStrategy = async (
    goal: string,
    competitorData: CompetitorAnalysisResult
): Promise<WriterConfig> => {
    const prompt = `
    VAI TRÒ: SEO Master Strategist - Bậc thầy xây dựng chiến lược nội dung.
    MỤC TIÊU CUỐI CÙNG: "${goal}"

    DỮ LIỆU ĐẦU VÀO:
    **Dữ liệu Phân tích Đối thủ:**
    ${JSON.stringify(competitorData, null, 2)}

    NHIỆM VỤ:
    Dựa vào TOÀN BỘ dữ liệu trên, hãy tổng hợp thành một bản kế hoạch chi tiết (WriterConfig) để AI Writer thực thi.
    
    YÊU CẦU ĐỊNH DẠNG ĐẦU RA (JSON - Chỉ trả về JSON, không Markdown):
    {
      "topic": "${goal}",
      "keyword": "${competitorData.keyword}",
      "secondaryKeywords": "List các từ khóa LSI quan trọng nhất, ngăn cách bởi dấu phẩy",
      "tone": "Chuyên nghiệp",
      "audience": "Xác định đối tượng mục tiêu dựa trên Search Intent",
      "length": "2500",
      "useEEAT": true,
      "competitorContext": "Tóm tắt ngắn gọn (2-3 câu) về chiến lược Skyscraper (Winning Angle, Hook, Gaps) từ dữ liệu đối thủ.",
      "customOutline": "Tạo một dàn ý (OUTLINE) cực kỳ chi tiết, ở định dạng Markdown, kết hợp giữa outline đề xuất của đối thủ và bổ sung các 'gapFillers' để tạo ra dàn ý vượt trội nhất."
    }
    `;

    try {
        const res = await ai.models.generateContent({
            model: 'gemini-4-argon', // Gemini 4.0 Argon flagship reasoning
            contents: prompt,
            config: { 
                responseMimeType: "application/json",
                thinkingConfig: { thinkingLevel: ThinkingLevel.HIGH }
            }
        });
        const defaultConfig: Partial<WriterConfig> = { 
            mode: 'create', type: 'blog', framework: 'none', model: 'gemini-4-argon' 
        };
        const parsedConfig = cleanAndParseJson(res.text, {});
        return { ...defaultConfig, ...parsedConfig } as WriterConfig;
    } catch (e: any) {
        throw new Error(formatAiError(e));
    }
};

export const optimizeContentSEO = async (content: string, keyword: string, issues: string[]): Promise<string> => {
    const prompt = `
    Bạn là chuyên gia tối ưu hóa SEO. Hãy sửa lại bài viết sau để khắc phục các vấn đề sau:
    ${issues.map(i => `- ${i}`).join('\n')}
    
    TỪ KHÓA CHÍNH: "${keyword}"
    NỘI DUNG GỐC:
    ${content}
    
    YÊU CẦU: Trả về bài viết đã tối ưu, giữ nguyên định dạng Markdown (Không có H1). Đảm bảo ngôn ngữ tự nhiên, không gượng ép khi chèn từ khóa.
    `;
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-4-argon',
            contents: prompt,
            config: { 
                systemInstruction: GLOBAL_SYSTEM_INSTRUCTION,
                thinkingConfig: { thinkingLevel: ThinkingLevel.HIGH }
            }
        });
        return res.text || content;
    } catch (e) { return content; }
};

export const suggestSeoMeta = async (content: string, topic: string, language: string = 'Vietnamese'): Promise<{ title: string, slug: string, description: string, tags: string[] }> => {
    const prompt = `
    VAI TRÒ: Chuyên gia tối ưu hóa tỷ lệ chuyển đổi (CRO) và SEO On-page hàng đầu (Top 1% Global).
    NHIỆM VỤ: Phân tích nội dung và "bào chế" bộ Metadata (Title, Slug, Description, Tags) kích thích người dùng click chuột ngay lập tức.

    NGÔN NGỮ ĐẦU RA: ${language}.
    CHỦ ĐỀ GỐC: "${topic}"
    NỘI DUNG (Context - 10k ký tự đầu):
    "${content.substring(0, 10000)}"

    --- YÊU CẦU TỐI ƯU HÓA (ALGORITHM V2) ---

    1. **SEO Title (Tiêu đề - Tối ưu CTR & Mobile):**
       - Độ dài: 50-60 ký tự (Không được cắt ngắn trên mobile).
       - Công thức: [Từ khóa chính] + [Lợi ích/Cảm xúc mạnh] + [Năm/Con số].
       - Phải chứa "Power Words" (VD: Mới nhất, Chi tiết, Từ A-Z, Bí mật, Sai lầm, Cấm kỵ).
       - Ví dụ tốt: "Học SEO 2025: Lộ trình 30 ngày từ Zero đến Hero (Có Case Study)"

    2. **URL Slug (Đường dẫn):**
       - Chỉ giữ lại từ khóa cốt lõi (Focus Keyword).
       - Loại bỏ 100% các từ nối (stopwords) như: là, của, những, các, và, tại, trong...
       - Ngắn gọn là vua (Short is King).
       - Định dạng: chu-thuong-gach-ngang.

    3. **Meta Description (Mô tả):**
       - Độ dài: 145-160 ký tự.
       - Công thức: [Vấn đề khách hàng gặp] -> [Giải pháp trong bài] -> [Kết quả đạt được] -> [CTA].
       - Phải chứa từ khóa chính ở 100 ký tự đầu.

    4. **Tags (Semantic & Entities):**
       - 5-8 từ khóa LSI hoặc thực thể liên quan để giúp Google hiểu ngữ cảnh (Context).
       - Không lặp lại y chang từ khóa chính.

    --- ĐỊNH DẠNG JSON OUTPUT (DUY NHẤT) ---
    {
        "title": "...",
        "slug": "...",
        "description": "...",
        "tags": ["...", "..."]
    }
    `;
    
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-4-argon', // UPGRADE: Use Gemini 4.0 Argon for high-reasoning SEO Metadata
            contents: prompt,
            config: { responseMimeType: "application/json" }
        });
        const result = cleanAndParseJson<any>(res.text, { title: topic, slug: '', description: '', tags: [] });
        
        // Ensure tags is always an array of strings
        let tags: string[] = [];
        if (Array.isArray(result.tags)) {
            tags = result.tags.map((t: any) => typeof t === 'string' ? t : String(t || ''));
        }
        
        return {
            title: typeof result.title === 'string' ? result.title : (result.title ? String(result.title) : topic),
            slug: typeof result.slug === 'string' ? result.slug : (result.slug ? String(result.slug) : ''),
            description: typeof result.description === 'string' ? result.description : (result.description ? String(result.description) : ''),
            tags: tags
        };
    } catch (e) { 
        console.error("Meta Gen Error", e);
        return { title: topic, slug: '', description: '', tags: [] }; 
    }
};

export const generateImage = async (prompt: string, aspectRatio: string = "16:9"): Promise<string | null> => {
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-3.1-flash-lite-image',
            contents: prompt,
            config: { imageConfig: { aspectRatio: aspectRatio } }
        });
        for (const p of res.candidates?.[0]?.content?.parts || []) {
            if (p.inlineData) return `data:image/png;base64,${p.inlineData.data}`;
        }
        return null;
    } catch (e) { return null; }
};

export const expandMindmapNode = async (pillarTopic: string, nodeLabel: string): Promise<{ keywords: string[] }> => {
    const prompt = `
    VAI TRÒ: Chuyên gia phân tích chủ đề.
    NHIỆM VỤ: Mở rộng (Expand) một chủ đề con trong bản đồ tư duy (mindmap).
    
    CHỦ ĐỀ GỐC (PILLAR): "${pillarTopic}"
    CHỦ ĐỀ CON CẦN MỞ RỘNG: "${nodeLabel}"

    YÊU CẦU:
    - Tìm 5-7 từ khóa hoặc chủ đề phụ cụ thể hơn, liên quan trực tiếp đến chủ đề con.
    - Đảm bảo các chủ đề phụ không bị trùng lặp với chủ đề gốc hoặc chủ đề con.
    - Chỉ trả về định dạng JSON.

    ĐỊNH DẠNG ĐẦU RA (JSON):
    {
        "keywords": ["từ khóa phụ 1", "từ khóa phụ 2", ...]
    }
    `;

    try {
        const res = await ai.models.generateContent({
            model: 'gemini-3.8-flash', // Flash is fine for this task
            contents: prompt,
            config: { responseMimeType: "application/json" }
        });
        return cleanAndParseJson(res.text, { keywords: [] });
    } catch (e) { throw new Error(formatAiError(e)); }
};

export const analyzeMindmapCoverage = async (pillarTopic: string, allKeywords: string[]): Promise<{ summary: string, suggestions: string[] }> => {
    const keywordsList = allKeywords.map(k => `- ${k}`).join('\n');
    const prompt = `
    VAI TRÒ: Chuyên gia chiến lược nội dung SEO.
    NHIỆM VỤ: Phân tích độ bao phủ chủ đề (Topic Coverage) của một bản đồ chiến lược.
    
    CHỦ ĐỀ GỐC (PILLAR): "${pillarTopic}"
    
    DANH SÁCH CÁC CHỦ ĐỀ HIỆN CÓ TRONG BẢN ĐỒ:
    ${keywordsList}

    YÊU CẦU PHÂN TÍCH:
    1.  **Tóm tắt (summary):** Đánh giá chung về độ bao phủ hiện tại. Liệu bộ từ khóa đã đầy đủ chưa? Có đi đúng hướng không?
    2.  **Gợi ý (suggestions):** Đề xuất 3-4 chủ đề con (sub-topics) quan trọng còn thiếu mà bản đồ chưa đề cập đến để tăng độ sâu và tính toàn diện cho chủ đề gốc.

    ĐỊNH DẠNG ĐẦU RA (JSON):
    {
        "summary": "Tóm tắt đánh giá của bạn...",
        "suggestions": ["Gợi ý chủ đề còn thiếu 1", "Gợi ý chủ đề còn thiếu 2", ...]
    }
    `;
    
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-4-argon', // Gemini 4.0 Argon for deep strategic analysis
            contents: prompt,
            config: { responseMimeType: "application/json" }
        });
        return cleanAndParseJson(res.text, { summary: '', suggestions: [] });
    } catch (e) { throw new Error(formatAiError(e)); }
};

export const auditContent = async (content: string, keyword: string): Promise<AuditResult> => {
    const prompt = `Kiểm toán nội dung bài viết dưới đây dựa trên từ khóa mục tiêu: "${keyword}".
    Phân tích: Score (0-100), Readability, Keyword Density, Suggestions (Action Plan), Semantic Gaps (Chủ đề còn thiếu), AI Personas, và đặc biệt là độ tuân thủ AI Search (SGE, ChatGPT Search).
    
    NỘI DUNG:
    ${content}
    
    YÊU CẦU KIỂM TOÁN AI SEARCH (aiSearchCompliance):
    - Chấm điểm (0-100) mức độ tối ưu cho AI Overview.
    - Bài viết có câu trả lời trực tiếp (Direct Answer/Key Takeaways) ở đầu bài không?
    - Có sử dụng bảng (Tables), danh sách (Bullet points) để AI dễ trích xuất dữ liệu không?
    - Có chứa Thông tin độc quyền (Information Gain) không?
    - Có FAQ ở cuối bài để đón bắt long-tail queries không?
    
    Trả về JSON:
    {
        "score": 85,
        "readability": "Dễ hiểu",
        "keywordDensity": [{"phrase": "...", "count": 5, "density": "1.2%"}],
        "suggestions": ["Sửa thẻ alt cho ảnh", "Thêm H3 cho đoạn X"],
        "missingTopics": [{"topic": "...", "advice": "..."}],
        "userSimulation": [{"persona": "Chuyên gia", "mood": "Hài lòng", "satisfactionScore": 90, "verdict": "..."}],
        "aiSearchCompliance": 80,
        "aiSearchTips": ["Thêm phần Key Takeaways ở đầu bài", "Chuyển đoạn X thành bảng"]
    }`;
    
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-4-argon',
            contents: prompt,
            config: { responseMimeType: "application/json" }
        });
        return cleanAndParseJson(res.text, { score: 0, readability: '', keywordDensity: [], suggestions: [], missingTopics: [], userSimulation: [], aiSearchCompliance: 0, aiSearchTips: [] });
    } catch (e) { throw new Error(formatAiError(e)); }
};

export const checkKeywordRank = async (keyword: string, domain: string, location: string): Promise<RankResult> => {
    // FIX: Enhanced Prompt to avoid hallucinations
    const prompt = `Kiểm tra thứ hạng (Rank) của domain "${domain}" cho từ khóa "${keyword}" trên Google Search tại vị trí "${location}". 
    
    QUY TẮC NGHIÊM NGẶT:
    1. Chỉ trả về Rank > 0 nếu bạn TÌM THẤY CHÍNH XÁC URL chứa domain "${domain}" trong danh sách kết quả tìm kiếm.
    2. Nếu không tìm thấy domain trong top 100, BẮT BUỘC phải trả về rank: 0. Không được tự đoán.
    3. Trả về URL chính xác của trang đích được xếp hạng.
    4. Ước tính Volume tìm kiếm nếu có thể.
    
    Trả về JSON:
    {
        "keyword": "${keyword}",
        "rank": 5,
        "url": "https://...",
        "volume": 1200
    }`;
    
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-3.8-flash',
            contents: prompt,
            config: { 
                tools: [{ googleSearch: {} }],
                responseMimeType: "application/json" 
            }
        });
        const result = cleanAndParseJson(res.text, { keyword, rank: 0, url: '', volume: 0 });
        return {
            ...result,
            lastChecked: new Date().toISOString(),
            history: []
        };
    } catch (e) { throw new Error(formatAiError(e)); }
};

export const generateSchemaMarkup = async (type: 'FAQ' | 'Article', content: string): Promise<string> => {
    const prompt = `Dựa trên nội dung sau, hãy tạo mã JSON-LD Schema loại "${type}" chuẩn Google Rich Snippets.
    Chỉ trả về chuỗi JSON thô bên trong thẻ script, không kèm theo thẻ script.
    
    NỘI DUNG:
    ${content.substring(0, 5000)}`;
    
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-3.8-flash',
            contents: prompt,
            config: { responseMimeType: "application/json" }
        });
        return res.text || '';
    } catch (e) { return ''; }
};

export const generateSocialPosts = async (platform: string, content: string): Promise<string> => {
    const prompt = `Chuyển đổi bài viết sau thành một bài đăng thu hút trên mạng xã hội ${platform}.
    Sử dụng emoji phù hợp, các hashtag thịnh hành và lời kêu gọi hành động (CTA).
    
    BÀI VIẾT:
    ${content.substring(0, 4000)}`;
    
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-3.8-flash',
            contents: prompt
        });
        return res.text || '';
    } catch (e) { return ''; }
};

export const generateImageSeo = async (base64Image: string): Promise<ImageSeoResult> => {
    const prompt = `Phân tích hình ảnh này và gợi ý các thông tin SEO:
    1. Alt Text (mô tả ngắn gọn, chứa từ khóa tiềm năng).
    2. Caption (chú thích ảnh sinh động).
    3. Filename chuẩn SEO (không dấu, cách nhau bằng gạch ngang).
    
    Trả về JSON:
    {
        "altText": "...",
        "caption": "...",
        "filename": "ten-file-anh-seo.jpg"
    }`;
    
    try {
        // Detect mime type from base64 string
        let mimeType = 'image/png';
        const match = base64Image.match(/^data:(image\/[a-zA-Z+]+);base64,/);
        if (match && match[1]) {
            mimeType = match[1];
        }

        const res = await ai.models.generateContent({
            model: 'gemini-3.8-flash',
            contents: {
                parts: [
                    { inlineData: { mimeType: mimeType, data: base64Image.split(',')[1] } },
                    { text: prompt }
                ]
            },
            config: { responseMimeType: "application/json" }
        });
        return cleanAndParseJson(res.text, { altText: '', caption: '', filename: '' });
    } catch (e) { throw new Error(formatAiError(e)); }
};

export const checkPlagiarism = async (content: string): Promise<PlagiarismResult> => {
    const prompt = `Kiểm tra xem nội dung sau có bị trùng lặp (đạo văn) trên internet hay không.
    Sử dụng Google Search để tìm kiếm các đoạn văn khớp.
    Đánh giá Score (0-100% Unique), trạng thái, danh sách các đoạn trùng lặp và URL nguồn, và phân tích chi tiết.
    
    NỘI DUNG CẦN CHECK:
    ${content.substring(0, 3000)}
    
    Trả về JSON:
    {
        "score": 95,
        "status": "Unique",
        "matches": [{"text": "...", "url": "...", "sourceTitle": "..."}],
        "analysis": "..."
    }`;
    
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-3.8-flash',
            contents: prompt,
            config: { 
                tools: [{ googleSearch: {} }],
                responseMimeType: "application/json" 
            }
        });
        return cleanAndParseJson(res.text, { score: 100, status: 'Unique', matches: [], analysis: '' });
    } catch (e) { throw new Error(formatAiError(e)); }
};

export const analyzeCompetitorStrategy = async (keyword: string, context: string = ''): Promise<CompetitorAnalysisResult> => {
    const prompt = `Thực hiện phân tích đối thủ chuyên sâu cho từ khóa: "${keyword}".
    ${context ? `Sử dụng ngữ cảnh website hiện tại: "${context}".` : ''}
    Tìm kiếm Top 3 đối thủ đang ranking, phân tích điểm mạnh và Content Gaps của họ.
    Xây dựng chiến lược Skyscraper (Góc tiếp cận độc đáo, Hook, Must-have topics, Outline đề xuất).
    
    Trả về JSON:
    {
        "keyword": "${keyword}",
        "difficultyLevel": "Medium",
        "searchIntent": "Informational",
        "topCompetitors": [{"title": "...", "url": "...", "wordCountEstimate": "1500 words", "strengths": ["..."], "contentGap": ["..."]}],
        "strategy": {
            "winningAngle": "...",
            "winningHook": "...",
            "mustHaveTopics": ["..."],
            "gapFillers": ["..."],
            "outline": "## H2 Header\\n### H3 Subheader..."
        }
    }`;
    
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-4-argon',
            contents: prompt,
            config: { 
                tools: [{ googleSearch: {} }],
                responseMimeType: "application/json" 
            }
        });
        return cleanAndParseJson(res.text, { keyword, difficultyLevel: '', searchIntent: '', topCompetitors: [], strategy: {} } as any);
    } catch (e) { throw new Error(formatAiError(e)); }
};

export const analyzeContentGaps = async (myDomain: string, competitorDomains: string[]): Promise<ContentGapResult> => {
    const prompt = `
    VAI TRÒ: Chuyên gia SEO Strategist.
    NHIỆM VỤ: Thực hiện phân tích lỗ hổng nội dung (Content Gap Analysis) chuyên sâu.
    
    DOMAIN CỦA TÔI: ${myDomain}
    CÁC ĐỐI THỦ CẠNH TRANH:
    ${competitorDomains.map((d, i) => `- Đối thủ ${i + 1}: ${d}`).join('\n')}

    QUY TRÌNH PHÂN TÍCH:
    1.  Sử dụng Google Search, tìm ra top 15-20 từ khóa quan trọng mà mỗi ĐỐI THỦ đang xếp hạng tốt (top 20).
    2.  Tổng hợp tất cả các từ khóa tìm được.
    3.  Với mỗi từ khóa, kiểm tra xem DOMAIN CỦA TÔI có xếp hạng trong top 50 hay không.
    4.  Phân loại các từ khóa vào 3 nhóm sau:
        -   **gaps (Lỗ hổng):** Từ khóa mà ít nhất 2 đối thủ xếp hạng, nhưng DOMAIN CỦA TÔI thì KHÔNG. Đây là cơ hội lớn nhất.
        -   **weaknesses (Điểm yếu):** Từ khóa mà cả tôi và đối thủ đều xếp hạng, nhưng thứ hạng của tôi thấp hơn đáng kể (ví dụ: tôi hạng 20+, đối thủ hạng <10). Đây là cơ hội tối ưu.
        -   **strengths (Điểm mạnh):** Từ khóa mà tôi xếp hạng cao hơn TẤT CẢ các đối thủ được liệt kê.

    TRẢ VỀ ĐỊNH DẠNG JSON (Không Markdown):
    {
      "gaps": [{"keyword": "...", "volume": "High/Medium/Low", "competitorRanks": [{"domain": "...", "rank": 1}]}],
      "weaknesses": [{"keyword": "...", "volume": "...", "myRank": 25, "competitorRanks": [{"domain": "...", "rank": 5}]}],
      "strengths": [{"keyword": "...", "volume": "...", "myRank": 3, "competitorRanks": [{"domain": "...", "rank": 15}]}]
    }
    `;

    try {
        const res = await ai.models.generateContent({
            model: 'gemini-4-argon', // Gemini 4.0 Argon for advanced multi-competitor reasoning
            contents: prompt,
            config: { 
                tools: [{ googleSearch: {} }],
                responseMimeType: "application/json",
                thinkingConfig: { thinkingLevel: ThinkingLevel.HIGH }
            }
        });
        return cleanAndParseJson(res.text, { gaps: [], weaknesses: [], strengths: [] });
    } catch (e) { throw new Error(formatAiError(e)); }
};

export const generateTopicClusterAI = async (seedKeyword: string, domainContext?: string): Promise<{
    clusterName: string;
    clusterKeywords: string[];
    pillar: { 
        topic: string; 
        keyword: string; 
        primaryKeyword: string;
        secondaryKeywords: string[]; 
        intent: string; 
        volume: string; 
        difficulty: number 
    };
    satellites: Array<{ 
        topic: string; 
        keyword: string; 
        primaryKeyword: string;
        satelliteKeyword: string;
        secondaryKeywords: string[]; 
        intent: string; 
        volume: string; 
        difficulty: number; 
        reason: string 
    }>;
}> => {
    const prompt = `
    VAI TRÒ: Chuyên gia Trưởng Chiến lược SEO (Chief SEO Content Architect).
    NHIỆM VỤ: Nghiên cứu, phân tích SERP và thiết kế 1 Topic Cluster (Cụm chủ đề Hub & Spoke) chuẩn SEO E-E-A-T cho từ khóa hạt nhân: "${seedKeyword}".
    ${domainContext ? `BỐI CẢNH WEBSITE: ${domainContext}` : ''}

    QUY TẮC PHÂN TÍCH VÀ ĐỀ XUẤT TỪ KHÓA BẮT BUỘC:
    1. BÀI TRỤ CỘT (PILLAR HUB PAGE):
       - "primaryKeyword" (Từ khóa chính Pillar): Từ khóa bao quát chủ đề hạt nhân có Search Volume lớn nhất (thường là "${seedKeyword}").
       - "secondaryKeywords" (Từ khóa phụ / LSI của Pillar): Danh sách 3 - 5 từ khóa phụ, từ khóa liên quan, biến thể tìm kiếm hoặc từ khóa bổ trợ ngữ cảnh cho bài Trụ Cột.
       - "topic": Tiêu đề bài viết Pillar toàn diện, hấp dẫn.
    
    2. CÁC BÀI VỆ TINH (SATELLITE SPOKES - từ 5 đến 8 bài):
       - "satelliteKeyword" (TỪ KHÓA VỆ TINH CHÍNH): Từ khóa ngách / long-tail tập trung sâu vào 1 Search Intent cụ thể của bài vệ tinh này.
       - "primaryKeyword": Trùng với "satelliteKeyword".
       - "secondaryKeywords" (Từ khóa phụ vệ tinh): Danh sách 2 - 4 từ khóa phụ, biến thể câu hỏi (LSI) của riêng bài vệ tinh này.
       - "topic": Tiêu đề bài viết vệ tinh giải quyết triệt để nhu cầu người tìm kiếm.
       - "reason": Lý do và cách bài vệ tinh này internal link đẩy topical authority về cho Pillar.

    3. ĐỊNH DẠNG TRẢ VỀ JSON THUẦN (KHÔNG KÈM MARKDOWN \`\`\`):
    {
      "clusterName": "Tên Cụm Chủ Đề (e.g. Cụm Chủ Đề: Đầu Tư Chứng Khoán Cơ Bản)",
      "clusterKeywords": ["từ khóa vệ tinh 1", "từ khóa vệ tinh 2", "từ khóa vệ tinh 3", "từ khóa vệ tinh 4", "từ khóa vệ tinh 5"],
      "pillar": {
        "topic": "Tiêu đề bài viết Pillar chính toàn diện",
        "primaryKeyword": "${seedKeyword}",
        "keyword": "${seedKeyword}",
        "secondaryKeywords": ["từ khóa phụ 1", "từ khóa phụ 2", "từ khóa phụ 3", "từ khóa LSI 4"],
        "intent": "Informational",
        "volume": "10K - 50K",
        "difficulty": 45
      },
      "satellites": [
        {
          "topic": "Tiêu đề bài vệ tinh 1",
          "satelliteKeyword": "từ khóa vệ tinh của bài 1",
          "primaryKeyword": "từ khóa vệ tinh của bài 1",
          "keyword": "từ khóa vệ tinh của bài 1",
          "secondaryKeywords": ["từ khóa phụ vệ tinh 1.1", "từ khóa phụ vệ tinh 1.2", "từ khóa LSI 1.3"],
          "intent": "Informational | Commercial | Transactional",
          "volume": "1K - 5K",
          "difficulty": 25,
          "reason": "Giải quyết thắc mắc A và đặt internal link ngược về Pillar"
        }
      ]
    }
    `;

    try {
        const res = await ai.models.generateContent({
            model: 'gemini-4-argon',
            contents: prompt,
            config: { 
                responseMimeType: "application/json" 
            }
        });
        const raw = cleanAndParseJson(res.text, { 
            clusterName: seedKeyword, 
            clusterKeywords: [],
            pillar: { 
                topic: seedKeyword, 
                keyword: seedKeyword, 
                primaryKeyword: seedKeyword, 
                secondaryKeywords: [], 
                intent: 'Informational', 
                volume: 'N/A', 
                difficulty: 30 
            }, 
            satellites: [] 
        });

        // Ensure robust structure
        const pillarPrimary = raw.pillar?.primaryKeyword || raw.pillar?.keyword || seedKeyword;
        const pillarSecondary = Array.isArray(raw.pillar?.secondaryKeywords) 
            ? raw.pillar.secondaryKeywords.map((k: any) => String(k).trim()).filter(Boolean)
            : [];

        const satellites = Array.isArray(raw.satellites) ? raw.satellites.map((s: any) => {
            const satPrimary = s.satelliteKeyword || s.primaryKeyword || s.keyword || s.topic || '';
            const satSecondary = Array.isArray(s.secondaryKeywords)
                ? s.secondaryKeywords.map((k: any) => String(k).trim()).filter(Boolean)
                : [];
            return {
                topic: s.topic || satPrimary,
                satelliteKeyword: satPrimary,
                primaryKeyword: satPrimary,
                keyword: satPrimary,
                secondaryKeywords: satSecondary,
                intent: s.intent || 'Informational',
                volume: s.volume || '1K - 5K',
                difficulty: typeof s.difficulty === 'number' ? s.difficulty : 25,
                reason: s.reason || 'Bổ trợ Topical Authority cho bài Trụ cột'
            };
        }) : [];

        const clusterKeywords = Array.isArray(raw.clusterKeywords) && raw.clusterKeywords.length > 0
            ? raw.clusterKeywords.map((k: any) => String(k).trim()).filter(Boolean)
            : satellites.map(s => s.satelliteKeyword);

        return {
            clusterName: raw.clusterName || seedKeyword,
            clusterKeywords,
            pillar: {
                topic: raw.pillar?.topic || seedKeyword,
                primaryKeyword: pillarPrimary,
                keyword: pillarPrimary,
                secondaryKeywords: pillarSecondary,
                intent: raw.pillar?.intent || 'Informational',
                volume: raw.pillar?.volume || '10K - 50K',
                difficulty: typeof raw.pillar?.difficulty === 'number' ? raw.pillar.difficulty : 40
            },
            satellites
        };
    } catch (e) {
        throw new Error(formatAiError(e));
    }
};

export const generateBrandVoiceSampleAI = async (profile: { brandName: string; toneVoice: string; targetAudience: string; personaDescription: string; knowledgeDocs?: string }): Promise<string> => {
    const prompt = `
    VAI TRÒ: ${profile.brandName} Brand Voice Copywriter.
    TÔNG GIỌNG (TONE): ${profile.toneVoice}
    ĐỐI TƯỢỢNG ĐỘC GIẢ: ${profile.targetAudience}
    PERSONA & PHONG CÁCH: ${profile.personaDescription}
    ${profile.knowledgeDocs ? `KIẾN THỨC NỀN TẢNG: ${profile.knowledgeDocs}` : ''}

    NHIỆM VỤ: Hãy viết một đoạn văn mẫu giới thiệu ngắn (150-200 từ) thể hiện rõ ràng và chuẩn xác 100% bản sắc Brand Voice này. Không dùng các từ ngữ AI slop sáo rỗng.
    `;

    try {
        const res = await ai.models.generateContent({
            model: 'gemini-3.8-flash',
            contents: prompt
        });
        return res.text || '';
    } catch (e) {
        throw new Error(formatAiError(e));
    }
};

export const checkSystemHealth = async (): Promise<{ status: string, latency: number, message: string }> => {
    const start = Date.now();
    try {
        const res = await ai.models.generateContent({
            model: 'gemini-3.8-flash',
            contents: 'ping',
// FIX: Removed maxOutputTokens config. Using it without a thinkingBudget can cause empty responses. For a simple health check, it's safer to omit it.
        });
        const latency = Date.now() - start;
        return { 
            status: res.text ? 'ok' : 'error', 
            latency, 
            message: res.text ? 'Kết nối Google Cloud AI ổn định.' : 'Không nhận được phản hồi từ AI.' 
        };
    } catch (e: any) {
        return { status: 'error', latency: Date.now() - start, message: e.message || 'Lỗi kết nối API.' };
    }
};